/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LojaPageContainer } from "../../common";
import {
  detalharPedidoLoja,
  atualizarStatusPedidoLoja,
  criarEnvioPedidoLoja,
  criarEventoEnvioPedidoLoja,
  atualizarEnvioPedidoLoja,
} from "../../../../../services/lojaPedidos";
import {
  IPedidoDetalhe,
  StatusPedido,
  IEnvioPedido,
  IEnderecoEntrega,
  IPagamentoLoja,
} from "interfaces/loja";
import {
  listarPagamentosLoja,
  atualizarStatusPagamentoLoja,
} from "../../../../../services/lojaPagamentos";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import ConfirmDialog from "components/ConfirmDialog";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMaterialUIController, setMessage } from "context";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";
import { formatDate } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import { labelProdutoPedidoItem } from "utils/formatFotoDisplayName";
import PedidoOperacionalHub from "./components/PedidoOperacionalHub";
import PedidoTimelineOperacional from "./components/PedidoTimelineOperacional";
import PedidoHistoricoTimeline from "./components/PedidoHistoricoTimeline";
import PedidoAcoesOperacionais from "./components/PedidoAcoesOperacionais";
import PedidoInteligenciaOperacional from "./components/PedidoInteligenciaOperacional";
import PedidoPagamentosDetalhe from "./components/PedidoPagamentosDetalhe";
import {
  extractPedidoDetalheResponse,
  messageFromApiError,
} from "utils/pedidoApiResponse";
import {
  buildPedidoOperacionalInput,
  isPedidoOperacionalImutavel,
} from "utils/pedidoOperacional";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { colors } from "@mui/material";

/** Textos do diálogo ao alterar status geral do pedido (PUT /loja/pedidos/status). */
function textoConfirmacaoStatusPedido(status: StatusPedido): {
  title: string;
  message: string;
  destructive: boolean;
  confirmLabel: string;
} {
  switch (status) {
    case "EM_PROCESSAMENTO":
      return {
        title: "Marcar pedido em processamento?",
        message:
          "O status geral deste pedido será alterado para Em processamento (preparação ou análise após o pagamento).",
        destructive: false,
        confirmLabel: "Confirmar",
      };
    case "ENVIADO":
      return {
        title: "Marcar pedido como enviado?",
        message:
          "O status geral deste pedido será alterado para Enviado, indicando que a mercadoria já foi despachada.",
        destructive: false,
        confirmLabel: "Confirmar",
      };
    case "ENTREGUE":
      return {
        title: "Marcar pedido como entregue?",
        message:
          "O status geral deste pedido será alterado para Entregue (entrega ao cliente concluída).",
        destructive: false,
        confirmLabel: "Confirmar",
      };
    case "CANCELADO":
      return {
        title: "Cancelar este pedido?",
        message:
          "O pedido passará ao status Cancelado na loja. Essa ação altera o andamento do pedido — confirme apenas se for o que deseja.",
        destructive: true,
        confirmLabel: "Sim, cancelar pedido",
      };
    default:
      return {
        title: "Atualizar status do pedido?",
        message: "Confirmar a alteração do status deste pedido?",
        destructive: false,
        confirmLabel: "Confirmar",
      };
  }
}

export default function LojaPedidoDetalhe() {
  const { pedidoId } = useParams();
  const navigate = useNavigate();
  const [, dispatch] = useMaterialUIController();
  const [detalhe, setDetalhe] = useState<IPedidoDetalhe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pagamentos, setPagamentos] = useState<IPagamentoLoja[]>([]);
  const [payLoading, setPayLoading] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState<{
    open: boolean;
    pagamentoId?: string;
    action?: "PAGO" | "CANCELADO" | "ESTORNADO";
    label?: string;
  }>({ open: false });
  const [confirmStatus, setConfirmStatus] = useState<{
    open: boolean;
    status?: StatusPedido;
    label?: string;
  }>({ open: false });
  const [confirmEnvio, setConfirmEnvio] = useState<{
    open: boolean;
    envioId?: string | number;
    label?: string;
  }>({ open: false });
  const [boletoPagoDlg, setBoletoPagoDlg] = useState<{
    open: boolean;
    pagamentoId?: string;
    motivo: string;
  }>({ open: false, motivo: "" });

  // Envio simples
  const [rast, setRast] = useState("");
  const [serv, setServ] = useState("");
  const [prov, setProv] = useState("");
  const [novoEvento, setNovoEvento] = useState<
    Record<string, { status?: string; descricao?: string; dataEvento?: string }>
  >({});
  const [envioEdit, setEnvioEdit] = useState<
    Record<string, { provedor?: string; servico?: string; rastreio?: string }>
  >({});

  useEffect(() => {
    (async () => {
      setLoadError(null);
      if (!pedidoId) {
        setDetalhe(null);
        setLoadError("URL sem ID do pedido.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data, status } = await detalharPedidoLoja(pedidoId);
        if (status === 200) {
          const pedido = extractPedidoDetalheResponse(data);
          if (pedido?.id) {
            setDetalhe(pedido);
          } else {
            setDetalhe(null);
            setLoadError(
              "Resposta do servidor não contém um pedido válido (verifique o formato da API)."
            );
          }
          return;
        }
        setDetalhe(null);
        const bodyMsg = messageFromApiError(
          data,
          status === 404
            ? "Pedido não encontrado."
            : `Não foi possível carregar o pedido (HTTP ${status}).`
        );
        if (status === 401) {
          setLoadError(
            "Sessão expirada ou não autenticado. Faça login novamente."
          );
        } else if (status === 403) {
          setLoadError(
            "Sem permissão para acessar este pedido. Verifique seu perfil (ex.: admin, financeiro, gerência)."
          );
        } else {
          setLoadError(bodyMsg);
        }
      } catch (error: unknown) {
        setDetalhe(null);
        const text =
          error instanceof Error
            ? error.message
            : "Falha ao carregar detalhes do pedido.";
        setLoadError(text);
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [pedidoId, dispatch]);

  useEffect(() => {
    (async () => {
      if (!pedidoId) return;
      try {
        setPayLoading(true);
        const { data, status } = await listarPagamentosLoja({ pedidoId });
        if (status === 200) {
          const arr = (data as any)?.pagamentos ?? (data as any)?.data ?? [];
          setPagamentos(Array.isArray(arr) ? arr : []);
        }
      } catch (error: any) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: error?.message || "Falha ao carregar pagamentos.",
        });
      } finally {
        setPayLoading(false);
      }
    })();
  }, [pedidoId]);

  const operacionalInput = useMemo(
    () =>
      detalhe
        ? buildPedidoOperacionalInput(
            detalhe as unknown as Parameters<
              typeof buildPedidoOperacionalInput
            >[0],
            pagamentos
          )
        : null,
    [detalhe, pagamentos]
  );
  const fluxoImutavel = Boolean(
    operacionalInput && isPedidoOperacionalImutavel(operacionalInput)
  );

  /** Evita dois “cancelar” na UI: POST operacional cobre estes status. */
  const temAcaoOperacionalPostNoDetalhe = Boolean(
    detalhe &&
    (detalhe.status === "AGUARDANDO_PAGAMENTO" ||
      detalhe.status === "PAGO" ||
      detalhe.status === "EM_PROCESSAMENTO" ||
      detalhe.status === "ENVIADO" ||
      detalhe.status === "ENTREGUE" ||
      detalhe.status === "DEVOLUCAO_SOLICITADA")
  );

  const recarregarPedidoAposAcaoOperacional = useCallback(async () => {
    if (!pedidoId) return;
    const [pedRes, paysRes] = await Promise.all([
      detalharPedidoLoja(pedidoId),
      listarPagamentosLoja({ pedidoId }),
    ]);
    if (pedRes.status === 200) {
      const pedido = extractPedidoDetalheResponse(pedRes.data);
      if (pedido) setDetalhe(pedido);
    }
    if (paysRes.status === 200) {
      const arr =
        (paysRes.data as any)?.pagamentos ?? (paysRes.data as any)?.data ?? [];
      setPagamentos(Array.isArray(arr) ? arr : []);
    }
  }, [pedidoId]);

  const cols: GridColDef[] = useMemo(
    () => [
      { field: "sku", headerName: "SKU", width: 140 },
      {
        field: "produtoNome",
        headerName: "Produto",
        flex: 1,
        minWidth: 240,
        valueGetter: (p) =>
          labelProdutoPedidoItem({
            sku: p.row.sku,
            produtoNome: p.row.produtoNome,
          }),
      },
      { field: "quantidade", headerName: "Qtd", width: 100 },
      {
        field: "precoUnitario",
        headerName: "Preço",
        width: 140,
        type: "number",
        valueGetter: (p) => Number(p.row.precoUnitario ?? 0),
        renderCell: (p) => (
          <MDTypography variant="body2">{formatCurrency(p.value)}</MDTypography>
        ),
      },
    ],
    []
  );

  async function onAtualizarStatus(status: StatusPedido) {
    if (!pedidoId) return;
    setSaving(true);
    try {
      await atualizarStatusPedidoLoja(pedidoId, status);
      const { data } = await detalharPedidoLoja(pedidoId);
      setDetalhe((data as any).data || (data as any));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: `Status atualizado para ${status}.`,
      });
    } finally {
      setSaving(false);
    }
  }

  async function onCriarEnvio() {
    if (!pedidoId) return;
    setSaving(true);
    try {
      await criarEnvioPedidoLoja(pedidoId, {
        provedor: prov,
        servico: serv,
        rastreio: rast,
      });
      const { data } = await detalharPedidoLoja(pedidoId);
      setDetalhe((data as any).data || (data as any));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Envio criado com sucesso.",
      });
      setProv("");
      setServ("");
      setRast("");
    } finally {
      setSaving(false);
    }
  }

  async function onChangePaymentStatus(
    pagamentoId: string,
    status: "PAGO" | "ESTORNADO" | "CANCELADO",
    extras?: { motivoBaixaManual?: string }
  ) {
    setPayLoading(true);
    try {
      await atualizarStatusPagamentoLoja(
        pagamentoId,
        status,
        extras?.motivoBaixaManual
          ? { motivoBaixaManual: extras.motivoBaixaManual }
          : undefined
      );
      // reload pagamentos e pedido
      if (pedidoId) {
        const [pays, ped] = await Promise.all([
          listarPagamentosLoja({ pedidoId }),
          detalharPedidoLoja(pedidoId),
        ]);
        const arr =
          (pays.data as any)?.pagamentos ?? (pays.data as any)?.data ?? [];
        setPagamentos(Array.isArray(arr) ? arr : []);
        setDetalhe((ped.data as any).data || (ped.data as any));
      }
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: `Status do pagamento atualizado para ${status}.`,
      });
      if (extras?.motivoBaixaManual) {
        setBoletoPagoDlg({ open: false, motivo: "" });
      }
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Falha ao atualizar o pagamento.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setPayLoading(false);
    }
  }

  return (
    <LojaPageContainer title="Pedidos" subtitle="Detalhe operacional">
      <MDBox mb={2}>
        <MDButton variant="outlined" color="dark" onClick={() => navigate(-1)}>
          Voltar
        </MDButton>
      </MDBox>

      {loading ? (
        <MDTypography variant="button">Carregando...</MDTypography>
      ) : detalhe ? (
        <>
          <PedidoAcoesOperacionais
            pedidoId={pedidoId!}
            detalhe={detalhe}
            status={detalhe.status}
            pagamentos={pagamentos}
            dispatch={dispatch}
            onPedidoRecarregado={recarregarPedidoAposAcaoOperacional}
          />

          {operacionalInput ? (
            <MDBox mb={3}>
              <PedidoTimelineOperacional input={operacionalInput} />
            </MDBox>
          ) : null}

          <PedidoOperacionalHub detalhe={detalhe} pagamentos={pagamentos} />

          {fluxoImutavel ? (
            <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
              Fluxo operacional encerrado: não é possível alterar status do
              pedido, envios ou eventos de envio por esta tela. Os dados abaixo
              permanecem apenas para consulta.
            </Alert>
          ) : (
            <MDBox display="flex" gap={1} mb={3} mt={3} flexWrap="wrap">
              <MDButton
                onClick={() =>
                  setConfirmStatus({
                    open: true,
                    status: "EM_PROCESSAMENTO",
                  })
                }
                disabled={saving}
              >
                Marcar em processamento
              </MDButton>
              <MDButton
                onClick={() =>
                  setConfirmStatus({
                    open: true,
                    status: "ENVIADO",
                  })
                }
                disabled={saving}
              >
                Marcar como enviado
              </MDButton>
              <MDButton
                onClick={() =>
                  setConfirmStatus({
                    open: true,
                    status: "ENTREGUE",
                  })
                }
                disabled={saving}
              >
                Marcar como entregue
              </MDButton>
              {!temAcaoOperacionalPostNoDetalhe ? (
                <MDButton
                  color="error"
                  onClick={() =>
                    setConfirmStatus({
                      open: true,
                      status: "CANCELADO",
                    })
                  }
                  disabled={saving}
                >
                  Cancelar pedido
                </MDButton>
              ) : null}
            </MDBox>
          )}

          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium">
              Itens
            </MDTypography>
            <DataGrid
              rows={detalhe.itens}
              getRowId={(r) => r.id}
              columns={cols}
              autoHeight
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </MDBox>

          {/* Endereços */}
          <MDBox
            mb={3}
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <MDBox p={2} sx={{ border: "1px solid #eee", borderRadius: 1 }}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{
                  backgroundColor: colors.grey[100],
                  p: 2,
                }}
              >
                Endereço de Entrega
              </MDTypography>
              {detalhe?.enderecoEntregaId ||
              (detalhe as any).enderecoEntrega ? (
                <MDBox mt={1}>
                  <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                    {(detalhe as any).enderecoEntrega?.nome || "-"}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    {(detalhe as any).enderecoEntrega?.logradouro},{" "}
                    {(detalhe as any).enderecoEntrega?.numero}{" "}
                    {(detalhe as any).enderecoEntrega?.complemento || ""}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    {(detalhe as any).enderecoEntrega?.bairro} -{" "}
                    {(detalhe as any).enderecoEntrega?.cidade}/
                    {(detalhe as any).enderecoEntrega?.uf}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    CEP {(detalhe as any).enderecoEntrega?.cep}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDTypography variant="caption">
                  Sem endereço de entrega.
                </MDTypography>
              )}
            </MDBox>
            <MDBox p={2} sx={{ border: "1px solid #eee", borderRadius: 1 }}>
              <MDTypography
                variant="button"
                fontWeight="medium"
                sx={{
                  backgroundColor: colors.grey[100],
                  p: 2,
                  borderRadius: 1,
                  width: "100%",
                }}
              >
                Endereço de Cobrança
              </MDTypography>
              {detalhe?.enderecoCobrancaId ||
              (detalhe as any).enderecoCobranca ? (
                <MDBox mt={1}>
                  <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                    {(detalhe as any).enderecoCobranca?.nome || "-"}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    {(detalhe as any).enderecoCobranca?.logradouro},{" "}
                    {(detalhe as any).enderecoCobranca?.numero}{" "}
                    {(detalhe as any).enderecoCobranca?.complemento || ""}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    {(detalhe as any).enderecoCobranca?.bairro} -{" "}
                    {(detalhe as any).enderecoCobranca?.cidade}/
                    {(detalhe as any).enderecoCobranca?.uf}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    CEP {(detalhe as any).enderecoCobranca?.cep}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDTypography variant="caption">
                  Sem endereço de cobrança.
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          {/* Envios */}
          {Array.isArray((detalhe as any).envios) && (
            <MDBox mb={3} id="sec-expedicao">
              <MDTypography variant="button" fontWeight="medium">
                Envios
              </MDTypography>
              {((detalhe as any).envios as IEnvioPedido[]).map((env) => (
                <MDBox
                  key={env.id}
                  mt={2}
                  p={2}
                  sx={{ border: "1px solid #eee", borderRadius: 1 }}
                >
                  <MDTypography variant="caption">
                    Provedor: {env.provedor || "-"}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    Serviço: {env.servico || "-"}
                  </MDTypography>
                  <br />
                  <MDTypography variant="caption">
                    Rastreio: {env.rastreio || "-"}
                  </MDTypography>
                  <br />
                  <MDBox display="flex" gap={1} mt={1}>
                    <MDInput
                      size="small"
                      label="Provedor"
                      value={envioEdit[env.id]?.provedor ?? env.provedor ?? ""}
                      disabled={fluxoImutavel || saving}
                      onChange={(e: any) =>
                        setEnvioEdit((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            provedor: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDInput
                      size="small"
                      label="Serviço"
                      value={envioEdit[env.id]?.servico ?? env.servico ?? ""}
                      disabled={fluxoImutavel || saving}
                      onChange={(e: any) =>
                        setEnvioEdit((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            servico: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDInput
                      size="small"
                      label="Rastreio"
                      value={envioEdit[env.id]?.rastreio ?? env.rastreio ?? ""}
                      disabled={fluxoImutavel || saving}
                      onChange={(e: any) =>
                        setEnvioEdit((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            rastreio: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="dark"
                      disabled={fluxoImutavel || saving}
                      onClick={() =>
                        setConfirmEnvio({
                          open: true,
                          envioId: env.id,
                        })
                      }
                    >
                      Atualizar envio
                    </MDButton>
                  </MDBox>

                  {/* Timeline de eventos, se existir */}
                  {Array.isArray((env as any).eventos) && (
                    <MDBox mt={2}>
                      <MDTypography variant="caption">Eventos:</MDTypography>
                      <Timeline sx={{ mt: 1, pl: 0 }}>
                        {((env as any).eventos as any[]).map(
                          (ev: any, index: number) => (
                            <TimelineItem key={ev.id || index}>
                              <TimelineSeparator>
                                <TimelineDot
                                  color={index === 0 ? "success" : "grey"}
                                />
                                {index <
                                  ((env as any).eventos as any[]).length -
                                    1 && <TimelineConnector />}
                              </TimelineSeparator>
                              <TimelineContent>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="medium"
                                >
                                  {ev.status || "-"}
                                </MDTypography>
                                <br />
                                <MDTypography variant="caption">
                                  {ev.descricao || "-"}
                                </MDTypography>
                                <br />
                                <MDTypography variant="caption" color="text">
                                  {ev.dataEvento
                                    ? formatDate(
                                        ev.dataEvento,
                                        "dd/MM/yyyy HH:mm:ss"
                                      )
                                    : "-"}
                                </MDTypography>
                              </TimelineContent>
                            </TimelineItem>
                          )
                        )}
                      </Timeline>
                    </MDBox>
                  )}

                  {/* Adicionar evento */}
                  <MDBox display="flex" gap={1} mt={1}>
                    <MDInput
                      size="small"
                      label="Status"
                      value={novoEvento[env.id]?.status ?? ""}
                      disabled={fluxoImutavel || saving}
                      onChange={(e: any) =>
                        setNovoEvento((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            status: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDInput
                      size="small"
                      label="Descrição"
                      value={novoEvento[env.id]?.descricao ?? ""}
                      disabled={fluxoImutavel || saving}
                      onChange={(e: any) =>
                        setNovoEvento((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            descricao: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDInput
                      size="small"
                      label="Data evento"
                      type="datetime-local"
                      disabled={fluxoImutavel || saving}
                      value={
                        novoEvento[env.id]?.dataEvento
                          ? String(novoEvento[env.id].dataEvento).slice(0, 16)
                          : ""
                      }
                      onChange={(e: any) =>
                        setNovoEvento((s) => ({
                          ...s,
                          [env.id]: {
                            ...(s[env.id] || {}),
                            dataEvento: e.target.value,
                          },
                        }))
                      }
                    />
                    <MDButton
                      size="small"
                      variant="gradient"
                      color="dark"
                      disabled={fluxoImutavel || saving}
                      onClick={async () => {
                        const ev = novoEvento[env.id];
                        const payload = {
                          status: ev?.status,
                          descricao: ev?.descricao,
                          dataEvento: (ev?.dataEvento
                            ? new Date(ev.dataEvento)
                            : new Date()
                          ).toISOString(),
                        };
                        setSaving(true);
                        try {
                          await criarEventoEnvioPedidoLoja(env.id, payload);
                          const { data } = await detalharPedidoLoja(pedidoId!);
                          setDetalhe((data as any).data || (data as any));
                          setNovoEvento((s) => ({ ...s, [env.id]: {} }));
                          setMessage(dispatch, {
                            open: true,
                            severity: "success",
                            text: "Evento adicionado.",
                          });
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      Adicionar evento
                    </MDButton>
                  </MDBox>
                </MDBox>
              ))}
            </MDBox>
          )}

          <PedidoPagamentosDetalhe
            pagamentos={pagamentos}
            valorPedido={detalhe.total}
            payLoading={payLoading}
            saving={saving}
            onMarcarPago={(pagamentoId, metodo) => {
              if (metodo === "BOLETO") {
                setBoletoPagoDlg({
                  open: true,
                  pagamentoId,
                  motivo: "",
                });
              } else {
                setConfirmDlg({ open: true, pagamentoId, action: "PAGO" });
              }
            }}
            onEstornar={(pagamentoId) =>
              setConfirmDlg({ open: true, pagamentoId, action: "ESTORNADO" })
            }
            onCancelar={(pagamentoId) =>
              setConfirmDlg({ open: true, pagamentoId, action: "CANCELADO" })
            }
          />

          {!fluxoImutavel && (
            <MDBox display="flex" flexDirection="column" gap={1}>
              <MDTypography variant="button" fontWeight="medium">
                Criar envio
              </MDTypography>
              <MDBox display="flex" gap={2}>
                <MDInput
                  label="Provedor"
                  value={prov}
                  onChange={(e: any) => setProv(e.target.value)}
                />
                <MDInput
                  label="Serviço"
                  value={serv}
                  onChange={(e: any) => setServ(e.target.value)}
                />
                <MDInput
                  label="Rastreio"
                  value={rast}
                  onChange={(e: any) => setRast(e.target.value)}
                />
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={onCriarEnvio}
                  disabled={saving}
                >
                  Criar envio
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {(detalhe.motivoCancelamento?.trim() ||
            detalhe.canceladoPorUserId?.trim()) && (
            <MDBox
              id="sec-dados-cancelamento"
              mb={3}
              p={2}
              sx={{ border: "1px solid #eee", borderRadius: 1 }}
            >
              <MDTypography variant="button" fontWeight="medium">
                Dados do cancelamento
              </MDTypography>
              <MDBox mt={1}>
                {detalhe.motivoCancelamento?.trim() ? (
                  <MDTypography variant="caption" display="block">
                    Motivo: {detalhe.motivoCancelamento.trim()}
                  </MDTypography>
                ) : null}
                {detalhe.canceladoPorUserId?.trim() ? (
                  <MDTypography variant="caption" display="block">
                    Cancelado por (ID do usuário):{" "}
                    {detalhe.canceladoPorUserId.trim()}
                  </MDTypography>
                ) : null}
                {detalhe.canceladoPor?.nome?.trim() ||
                detalhe.canceladoPor?.email?.trim() ? (
                  <MDTypography variant="caption" display="block">
                    Usuário:{" "}
                    {detalhe.canceladoPor?.nome?.trim() ||
                      detalhe.canceladoPor?.email?.trim()}
                  </MDTypography>
                ) : null}
              </MDBox>
            </MDBox>
          )}

          <MDBox mt={4}>
            <PedidoHistoricoTimeline itens={detalhe.historicoPedidos ?? []} />
          </MDBox>
        </>
      ) : (
        <MDBox>
          <MDTypography variant="button" color="error" display="block" mb={1}>
            {loadError ?? "Pedido não encontrado."}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            display="block"
          >
            O detalhe do pedido (linha do tempo, cabeçalho e ações) aparece
            assim que a API retornar os dados. Se o erro persistir, confira o
            token, a URL do backend (REACT_APP_ENDPOINT) e se este pedido existe
            na base.
          </MDTypography>
        </MDBox>
      )}

      <ConfirmDialog
        open={confirmDlg.open}
        title="Confirmar"
        onClose={() => setConfirmDlg({ open: false })}
        onConfirm={() => {
          if (confirmDlg.pagamentoId && confirmDlg.action) {
            onChangePaymentStatus(confirmDlg.pagamentoId, confirmDlg.action);
          }
          setConfirmDlg({ open: false });
        }}
      />

      <Dialog
        open={boletoPagoDlg.open}
        onClose={() => {
          if (!payLoading) setBoletoPagoDlg({ open: false, motivo: "" });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Baixa manual do boleto</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            A cobrança será liquidada na Efí e o pagamento marcado como pago.
            Informe o motivo da baixa manual (registo interno).
          </MDTypography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo da baixa manual"
            value={boletoPagoDlg.motivo}
            onChange={(e) =>
              setBoletoPagoDlg((s) => ({ ...s, motivo: e.target.value }))
            }
            disabled={payLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setBoletoPagoDlg({ open: false, motivo: "" })}
            disabled={payLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              payLoading ||
              !boletoPagoDlg.pagamentoId ||
              !boletoPagoDlg.motivo.trim()
            }
            onClick={() => {
              const id = boletoPagoDlg.pagamentoId;
              const m = boletoPagoDlg.motivo.trim();
              if (!id || !m) return;
              void onChangePaymentStatus(id, "PAGO", {
                motivoBaixaManual: m,
              });
            }}
          >
            Liquidar na Efí e marcar pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação para atualização de Status do Pedido */}
      <ConfirmDialog
        open={confirmStatus.open}
        {...(confirmStatus.status
          ? textoConfirmacaoStatusPedido(confirmStatus.status)
          : {
              title: "Atualizar status?",
              message: "Confirmar a alteração do status deste pedido?",
              destructive: false,
              confirmLabel: "Confirmar",
            })}
        cancelLabel="Não"
        onClose={() => setConfirmStatus({ open: false })}
        onConfirm={() => {
          if (confirmStatus.status) onAtualizarStatus(confirmStatus.status);
          setConfirmStatus({ open: false });
        }}
      />

      {/* Diálogo de confirmação para Atualização de Envio */}
      <ConfirmDialog
        open={confirmEnvio.open}
        title="Confirmar"
        onClose={() => setConfirmEnvio({ open: false })}
        onConfirm={async () => {
          if (!confirmEnvio.envioId || !pedidoId)
            return setConfirmEnvio({ open: false });
          setSaving(true);
          try {
            await atualizarEnvioPedidoLoja(
              confirmEnvio.envioId as any,
              (envioEdit as any)[confirmEnvio.envioId]
            );
            const { data } = await detalharPedidoLoja(pedidoId);
            setDetalhe((data as any).data || (data as any));
            setMessage(dispatch, {
              open: true,
              severity: "success",
              text: "Envio atualizado.",
            });
          } finally {
            setSaving(false);
            setConfirmEnvio({ open: false });
          }
        }}
      />
    </LojaPageContainer>
  );
}
